/* eslint-disable no-useless-escape */
import { ChangeEvent, useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { TextInput, PasswordErrorList, Button } from "../..";
import DropArea from "../../DropArea/DropArea";
import FormErrorMessage from "../../FormErrorMessage";
import OtpComponent from "../../OtpComponent";

import { AppDispatch, RootState } from "../../../redux/store";
import { acceptInvitation } from "../../../redux/slices/UserSlice";
import { AuthContext } from "../../../contexts/AuthContext/AuthContext";
import { ToastContext } from "../../../contexts/ToastContext/ToastContext";
import { validatePassword, onFileError } from "../../../utils";
import { SESSION_STORAGE_SHOW_MFA } from "../../../utils/constants";
import { FileTypes, ToastVariants } from "../../../models";

export interface PasswordDataType {
  id: string;
  placeholder: string;
  required: boolean;
  errorMesage?: string;
  label: string;
  value: string;
  isValid?: boolean;
  // Optional fields used for non-password inputs (mpcNumber, signImageFileId)
  errorMsg?: string;
  isError?: boolean;
  type?: "password" | "text" | "file";
}

const validationsInitData = [
  {
    id: "lowerCase",
    isValid: false,
    msg: "Password must contain a lower case letter",
  },
  {
    id: "upperCase",
    isValid: false,
    msg: "Password must contain an upper case letter",
  },
  {
    id: "specialCharacter",
    isValid: false,
    msg: "Password must contain a special character",
  },
  { id: "cotainNumber", isValid: false, msg: "Password must contain a number" },
  {
    id: "eightCharacters",
    isValid: false,
    msg: "Password must contain at least  8 characters",
  },
  { id: "passwordMatch", isValid: false, msg: "Password must match" },
];

const PasswordConfirmationForm = () => {
  const auth = useContext(AuthContext);
  const toast = useContext(ToastContext);
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const [Ispcp, setIsPcp] = useState<boolean | any>(false);
  const buildPasswordInitData = (isPcp: boolean): PasswordDataType[] => {
    const baseFields: PasswordDataType[] = [
      {
        id: "password",
        label: "Password",
        placeholder: "Enter Password",
        required: true,
        errorMesage: "Please provide password",
        value: "",
        isValid: false,
        type: "password",
      },
      {
        id: "confirmPassword",
        label: "Confirm Password",
        placeholder: "Confirm password",
        required: true,
        errorMesage: "Password and confirm password are not same",
        value: "",
        isValid: false,
        type: "password",
      },
    ];

    if (!isPcp) {
      return baseFields;
    }

    return [
      ...baseFields,
      {
        id: "mpcNumber",
        label: "Medical practitioner Number (CPSO, CPSBC, CPSNS etc)",
        placeholder: "Enter medical practitioner number",
        required: true,
        value: "",
        errorMsg: "MPN is required and can only contain letters and numbers (max 12 characters).",
        isError: false,
        type: "text",
      },
      {
        id: "signImageFileId",
        label: "Signature",
        placeholder: "Upload signature",
        required: true,
        value: "",
        errorMsg: "Signature is required.",
        isError: false,
        type: "file",
      },
    ];
  };

  const [passwordData, setPasswordData] = useState<PasswordDataType[]>(() => buildPasswordInitData(Ispcp));
  const [validations, setValidations] = useState(validationsInitData);
  const [touchedInput, setTouchedInput] = useState(false);
  const [formErrorInitiated, setFormErrorInitiated] = useState(false);
  const [showMfa, setShowMfa] = useState(false);
  const [otpInput, setOtpInput] = useState("");
  const [loadingOtp, setLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const { loading, isUserAccepted, invitationTokenDetails, error } = useSelector((state: RootState) => state.user);
  const [signImageFileId, setSignImageFileId] = useState<string | undefined>();
  const [signatureDropAreaError, setSignatureDropAreaError] = useState<string>("");
  useEffect(() => {
    const userId = invitationTokenDetails?.userId;
    const isPrimaryCareProvider = invitationTokenDetails?.offices?.some(office =>
      office?.primaryCareProviders?.some(provider => provider?.userId === userId)
    );
    setIsPcp(isPrimaryCareProvider);
  }, [invitationTokenDetails]);

  // Rebuild the fields when Ispcp changes so MPC and signature fields appear/disappear accordingly
  useEffect(() => {
    setPasswordData(prev => {
      const next = buildPasswordInitData(Ispcp);
      // Preserve existing values for fields with the same id (e.g., password/confirmPassword)
      return next.map(field => {
        const existing = prev.find(f => f.id === field.id);
        return existing ? { ...field, value: existing.value, isError: existing.isError } : field;
      });
    });
  }, [Ispcp]);

  const keyUpHandler = (e: any) => {
    if (e.key === "Enter" && e.target.name === "confirmPassword") {
      !loading && isFormValid() ? handleLoginPress() : null;
    }
  };

  const handlePasswordChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.currentTarget.value;
    const fieldId = e.currentTarget.name;

    if (fieldId === "confirmPassword") {
      const isValid = passwordData[0].value === value;

      const validationErrors = [...validations];
      validationErrors[5] = { ...validationErrors[5], isValid };
      setValidations(validationErrors);
    } else if (fieldId === "password") {
      const newValidations = validations.map(item => {
        const isValid = validatePassword(item.id, value);

        return { ...item, isValid };
      });
      setValidations(newValidations);
    } else if (fieldId === "mpcNumber") {
      // Allow only alphanumeric, max 12 chars
      const alphanumericValue = value.replace(/[^a-zA-Z0-9]/g, "").slice(0, 12);
      const trimmedValue = alphanumericValue.trim();

      // Required, no minimum length (other than non-empty), max 12, alphanumeric only
      const isError = trimmedValue === "" || !/^[a-zA-Z0-9]+$/.test(trimmedValue) || trimmedValue.length > 12;

      setPasswordData(prev => {
        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          value: alphanumericValue,
          isError,
        };
        return updated;
      });
      return;
    }

    setPasswordData(prev => {
      const updated = [...prev];
      updated[index] = {
        ...updated[index],
        value,
      };
      return updated;
    });
  };

  const handleLoginPress = () => {
    setFormErrorInitiated(true);
    if (!isFormValid()) {
      return;
    }
    setTouchedInput(false);
    const mpcNumberValue = passwordData.find(f => f.id === "mpcNumber")?.value || "";

    dispatch(
      acceptInvitation({
        invitationToken: invitationTokenDetails?.token || "",
        password: passwordData[0].value,
        mpcNumber: mpcNumberValue,
        signImageFileId,
      })
    );
  };

  const handleSignIn = async (resend?: boolean) => {
    if (invitationTokenDetails?.email) {
      const signIn = await auth?.signin({ email: invitationTokenDetails?.email, password: passwordData[0].value });
      if (signIn) {
        if (signIn.emailVerificationRequired) {
          setShowMfa(true);
          if (resend) {
            toast?.openToast("A new code has been sent to your email.", 2000, ToastVariants.Success);
          }
          return;
        }
        navigate("/");
      } else {
        toast?.openToast("Something went wrong. Please try again!");
      }
    } else {
      toast?.openToast("Something went wrong. Please try again!");
    }
  };

  const handleSubmitOTP = async () => {
    if (invitationTokenDetails?.email) {
      setLoading(true);
      setShowError(false);
      const email = invitationTokenDetails?.email;
      const res = await auth?.authenticateMFA(email.trim(), otpInput);
      if (res) {
        setShowMfa(false);
        setOtpInput("");
        navigate("/");
      } else {
        setShowError(true);
      }
      setLoading(false);
    }
  };

  useEffect(() => {
    if (passwordData[0].value.length > 0 || passwordData[1].value.length > 0) {
      setTouchedInput(true);
    } else {
      setTouchedInput(false);
    }
  }, [passwordData]);

  useEffect(() => {
    if (isUserAccepted) {
      handleSignIn();
    }
  }, [isUserAccepted]);

  const isFormValid = () => {
    const passwordsValid = validations.every(it => it.isValid);
    const mpcField = passwordData.find(f => f.id === "mpcNumber");
    const mpcValid =
      !Ispcp ||
      !mpcField ||
      (typeof mpcField.value === "string" &&
        mpcField.value.trim() !== "" &&
        /^[a-zA-Z0-9]+$/.test(mpcField.value.trim()) &&
        mpcField.value.trim().length <= 12 &&
        !mpcField.isError);

    const signatureValid = !Ispcp || !!signImageFileId;

    return passwordsValid && mpcValid && signatureValid;
  };

  return (
    <div className="w-full md:max-w-md mt-0">
      {!showMfa ? (
        <>
          <h3 className="heading text-center">Welcome to Docnote!</h3>
          {error || isUserAccepted === false ? (
            <p className="text-red-700" style={{ color: "red" }}>
              An error has occurred, please try again
            </p>
          ) : (
            <p className="text-base font-medium text-center">Create a new password to access your account.</p>
          )}
          <div className="py-5">
            {passwordData.map((item, index) => {
              if (item.type === "file") {
                return (
                  <div className="py-2" key={item.id}>
                    <label className="text-sm text-docsigna-blue-dark font-medium block w-full max-w-full mb-1">
                      {item.label}
                      {item.required && <span className="text-red-500">*</span>}
                    </label>
                    <DropArea
                      allowedFileTypes={[FileTypes.PNG]}
                      maxFileSize={100 * 1024}
                      maxNumOfFiles={1}
                      dropAreaText="PNG signature (max 100KB)"
                      isDarkBg={false}
                      isLocalUpload
                      onFilesChange={fileIds => {
                        if (!fileIds.length) return;
                        const latestId = fileIds[fileIds.length - 1];
                        setSignImageFileId(latestId);
                      }}
                      handleFileChange={() => {
                        // Clear previous errors when a new file is selected
                        setSignatureDropAreaError("");
                      }}
                      onError={err => {
                        setSignatureDropAreaError(onFileError(err, "100kb"));
                      }}
                    />
                    {formErrorInitiated && !signImageFileId && <FormErrorMessage text={item.errorMsg || "Signature is required."} />}
                    {signatureDropAreaError && <p className="text-red-500 text-sm mt-1">{signatureDropAreaError}</p>}
                  </div>
                );
              }

              const isPasswordField = item.type === "password";

              return (
                <div className="py-2" key={item.id}>
                  <TextInput
                    isRequired={item.required}
                    label={item.label}
                    value={item.value}
                    placeholder={item.placeholder}
                    name={item.id}
                    isPassword={isPasswordField}
                    maxLength={item.id === "mpcNumber" ? 12 : undefined}
                    onFocus={() => {}}
                    onChangeFunc={e => handlePasswordChange(e, index)}
                    onKeyUp={e => keyUpHandler(e)}
                    isError={formErrorInitiated && !!item.isError}
                    errorMsg={item.errorMsg}
                  />
                </div>
              );
            })}
            <div>
              {touchedInput
                ? validations.map(item => {
                    return (
                      <div key={item.id} className="my-1 ml-2">
                        <PasswordErrorList isValid={item.isValid} message={item.msg} />
                      </div>
                    );
                  })
                : null}
            </div>
            <div className="py-2 mt-5">
              <Button
                variant={loading ? "loading" : "Primary"}
                onClick={() => (!loading ? handleLoginPress() : null)}
                text={"Log In"}
                disabled={!isFormValid()}
              />
            </div>
          </div>
        </>
      ) : (
        <OtpComponent
          value={invitationTokenDetails?.email ?? "email"}
          otpInput={otpInput}
          setOtpInput={setOtpInput}
          loading={loadingOtp}
          showError={showError}
          submit={handleSubmitOTP}
          resend={() => handleSignIn(true)}
        />
      )}
    </div>
  );
};

export default PasswordConfirmationForm;
